import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, Download, Share2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useToast } from "@/components/ui/use-toast";
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

// Mock course data for certificate
interface CourseData {
  id: string;
  title: string;
  description: string;
}

const MOCK_COURSES: Record<string, CourseData> = {
  '1': {
    id: '1',
    title: 'Introduction to Web Development',
    description: 'Mastering the fundamentals of HTML, CSS, and JavaScript to build modern websites',
  },
  '2': {
    id: '2',
    title: 'Responsive Design Principles',
    description: 'Creating websites that work flawlessly across all devices and screen sizes',
  },
};

const Certificate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const certificateRef = useRef<HTMLDivElement>(null);
  const [course, setCourse] = useState<CourseData | null>(null);
  const [completionDate, setCompletionDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);

  // Use the auth redirect hook
  useAuthRedirect(false);
  
  // Effect for fetching data
  useEffect(() => {
    // Fetch data only if authenticated
    if (isAuthenticated && user && id) {
      // In a real app, fetch course data and check completion status from API
      const timer = setTimeout(() => {
        if (MOCK_COURSES[id]) {
          setCourse(MOCK_COURSES[id]);
          // TODO: Replace localStorage check with API call for completion status
          setIsLoading(false);
        } else {
          toast.error('Course not found');
          navigate('/dashboard');
        }
      }, 500);
      
      return () => clearTimeout(timer);
    } else if (!isAuthenticated) {
       // Hook handles redirect, set loading false
       setIsLoading(false);
    }
  }, [id, isAuthenticated, navigate, user, toast]);

  if (isLoading || !course) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 container py-6">
          <div className="flex flex-col items-center justify-center h-80">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading certificate...</p>
          </div>
        </main>
      </div>
    );
  }
  
  // Format date to readable string
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  
  // Generate a unique certificate ID
  const certificateId = `${course.id}-${user?.id}-${Date.now().toString(36)}`;
  
  // Share certificate (mock functionality)
  const shareCertificate = () => {
    toast.success('Certificate link copied to clipboard');
  };
  
  // Download certificate (mock functionality)
  const downloadCertificate = () => {
    toast.success('Certificate downloaded successfully');
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Button variant="ghost" asChild className="mb-4">
          <Link to={`/course/${course.id}`} className="inline-flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Course
          </Link>
        </Button>
        
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Award className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Congratulations!
          </h1>
          <p className="mt-2 text-xl text-muted-foreground">
            You've successfully completed the course
          </p>
        </div>
        
        {/* Certificate */}
        <div 
          ref={certificateRef}
          className="mx-auto max-w-3xl rounded-lg border bg-card p-8 shadow-lg"
        >
          <div className="flex flex-col items-center justify-center space-y-6 rounded-md border-4 border-double border-primary/20 bg-background p-8 text-center">
            <div className="text-sm font-light uppercase tracking-wider text-muted-foreground">
              Certificate of Completion
            </div>
            
            <Award className="h-16 w-16 text-primary" />
            
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">This certificate is awarded to</div>
              <div className="text-2xl font-bold">{user?.fullName}</div>
            </div>
            
            <div className="space-y-2 max-w-lg">
              <div className="text-sm text-muted-foreground">For successfully completing</div>
              <div className="text-xl font-semibold">{course.title}</div>
              <div className="text-sm text-muted-foreground">{course.description}</div>
            </div>
            
            <div className="pt-4 text-sm text-muted-foreground">
              Completed on {formatDate(completionDate)}
            </div>
            
            <div className="w-full border-t border-border pt-4 text-xs text-muted-foreground">
              Certificate ID: {certificateId}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="mt-8 flex flex-col items-center justify-center space-y-4">
          <div className="flex flex-wrap gap-4 justify-center">
            <Button onClick={downloadCertificate} className="flex items-center">
              <Download className="mr-2 h-4 w-4" />
              Download Certificate
            </Button>
            <Button variant="outline" onClick={shareCertificate} className="flex items-center">
              <Share2 className="mr-2 h-4 w-4" />
              Share Certificate
            </Button>
          </div>
          
          <div className="text-center text-sm text-muted-foreground">
            Continue your learning journey by exploring more courses
          </div>
          
          <Button variant="ghost" asChild>
            <Link to="/dashboard">
              View All Courses
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Certificate;
